import { clearQueryCache, readQuery } from './queryCache';
import { ECOMMERCE_API_BASE_URL } from '../config';
import { apiErrorMessage, NETWORK_ERROR_MESSAGE, readErrorBody } from './errorMessage';
import { getSupplierToken, clearSupplierSession, SupplierRole, SupplierInfo } from '../features/supplier/supplierAccess';

const BASE = `${ECOMMERCE_API_BASE_URL}/supplier`;

export class SupplierApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = 'SupplierApiError';
        this.status = status;
    }
}

export interface SupplierProduct {
    id: number;
    supplier_id: number;
    item_code: string;
    name: string | null;
    price: number | null;
    price_currency: string;
    quantity: number;
    height: number | null;
    width: number | null;
    depth: number | null;
    weight: number | null;
    category_id: number | null;
    category_name: string | null;
    product_labels: string[];
    product_note: string | null;
    brand_id: number | null;
    brand_name: string | null;
    /** 服务端判定的采购完整性。品牌缺失会挡住接单，所以网页也要能看到。 */
    purchase_ready: boolean;
    missing_purchase_fields: string[];
    front_url: string | null;
    bottom_url: string | null;
    bottom_not_applicable: boolean;
    other_urls: string[];
    status: string;
    // ── 商店 platform richer contract (Phase B) ──
    supply_price: number | null;
    retail_price: number | null;
    supplier_sku: string | null;
    withpot_sku: string;
    internal_size_cm: { length: number | null; width: number | null; height: number | null };
    external_size_cm: { length: number | null; width: number | null; height: number | null };
    attributes: {
        drainage_hole?: string; saucer?: string; material?: string;
        selling_points?: string[]; description?: string;
    };
    detail_url: string | null;
    /** Detail-sized images (1200px). */
    photos: { front: string | null; detail: string | null; bottom: string | null };
    /** Grid tiles (600px) — a fraction of the bytes, for list rendering. */
    thumbnails: { front: string | null; detail: string | null; bottom: string | null };
    client_id: string | null;
    revision: number;
    created_at: string | null;
    updated_at: string | null;
}

export type PhotoSlot = 'front' | 'detail' | 'bottom';

export interface PhotoUploadTicket {
    token: string;
    key: string;
    public_url: string;
    upload_url: string;
    slot: PhotoSlot;
}

export interface SupplierSummary extends SupplierInfo {
    product_count: number;
    has_access_code?: boolean;
    has_password?: boolean;
    /** 登录手机号。密码永远不回传——只能重设，不能读出来。 */
    phone?: string | null;
    // Up to 4 recent product front photos for the store-directory preview.
    thumbnails?: string[];
    // Present only in team-authenticated responses (plaintext code, team-approved).
    access_code?: string | null;
}

export interface LoginResult {
    token: string;
    role: SupplierRole;
    supplier: SupplierInfo | null;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
    const token = getSupplierToken();
    return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

// fetch() throws a raw TypeError ("Failed to fetch") when the server is
// unreachable — translate that to a Chinese message so nothing English reaches
// the page.
async function doFetch(input: string, init?: RequestInit): Promise<Response> {
    try {
        const response = await fetch(input, init);
        if (response.ok && !['GET', 'HEAD'].includes((init?.method ?? 'GET').toUpperCase())) clearQueryCache();
        return response;
    } catch (error) {
        if (error instanceof Error && error.name === "AbortError") throw error;
        throw new SupplierApiError(NETWORK_ERROR_MESSAGE, 0);
    }
}

async function readError(res: Response): Promise<string> {
    return apiErrorMessage(res.status, await readErrorBody(res));
}

async function handle<T>(res: Response): Promise<T> {
    if (res.status === 401) {
        clearSupplierSession();
        // Notify the layout to bounce back to login.
        window.dispatchEvent(new Event('supplierAuthExpired'));
    }
    if (!res.ok) throw new SupplierApiError(await readError(res), res.status);
    return res.json();
}

// ── Auth ────────────────────────────────────────────────────────────────────
// Log in. Suppliers use 手机号(account) + 密码(password); the team master code is
// entered in `account` with no password. A bare code (legacy access_code) also works.
export async function supplierLogin(account: string, password?: string): Promise<LoginResult> {
    const res = await doFetch(`${BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, password: password || undefined }),
    });
    return handle<LoginResult>(res);
}

// PLACEHOLDER SMS flow: request a verification code for password reset. In dev the
// backend returns `dev_code`; in production the code is sent by 阿里云 SMS only.
export async function requestSupplierSmsCode(phone: string): Promise<{ ticket: string; dev_code?: string }> {
    const res = await doFetch(`${BASE}/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
    });
    return handle<{ ticket: string; dev_code?: string }>(res);
}

export async function resetSupplierPassword(input: {
    phone: string; ticket: string; code: string; new_password: string;
}): Promise<{ reset: boolean }> {
    const res = await doFetch(`${BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
    });
    return handle<{ reset: boolean }>(res);
}

// ── Orders (pushed as soon as a 采购 line exists — 待接单) ────────────────────
/** 采购 line status, mirrored onto the item the supplier answers. */
export type SupplierItemStatus =
    | 'pending_purchase' | 'purchased' | 'receiving' | 'completed'
    | 'out_of_stock' | 'exception';

export interface SupplierOrderItem {
    id: number;
    procurement_line_id: number | null;
    product_master_id: number | null;
    name: string | null;
    /** What the team is asking for. */
    quantity: number;
    /** What the supplier agreed to — null until they answer. */
    accepted_quantity: number | null;
    unit_price: number | null;
    status: SupplierItemStatus | null;
    status_label: string | null;
    is_pending_acceptance: boolean;
    shipping_carrier: string | null;
    tracking_number: string | null;
    shipped_at: string | null;
}

export type SupplierOrderStatus =
    | 'pending' | 'accepted' | 'shipped' | 'received' | 'declined';

export interface SupplierOrder {
    id: number;
    supplier_id: number;
    supplier_name: string | null;
    procurement_order_id: number | null;
    order_code: string;
    status: SupplierOrderStatus;
    shipping_carrier: string | null;
    tracking_number: string | null;
    shipped_at: string | null;
    /** Delivery details snapshotted from the target warehouse at push time. */
    recipient_name: string | null;
    recipient_phone: string | null;
    ship_to_address: string | null;
    items: SupplierOrderItem[];
    /** How many lines still need an answer, and how many have left the workshop. */
    pending_acceptance_count: number;
    shipped_count: number;
    total_quantity: number;
    total_amount: number;
    created_at: string | null;
    updated_at: string | null;
}

export async function listSupplierOrders(supplierId?: number): Promise<{ orders: SupplierOrder[] }> {
    const q = supplierId != null ? `?supplier_id=${supplierId}` : '';
    const res = await doFetch(`${BASE}/orders${q}`, { headers: authHeaders() });
    return handle<{ orders: SupplierOrder[] }>(res);
}

/** 接单: how many of this line the supplier can make, and at what price.
 *  Zero declines it (→ 缺货). Both endpoints return the whole updated order. */
export async function acceptSupplierOrderItem(
    orderId: number, itemId: number,
    input: { accepted_quantity: number; unit_price?: number },
): Promise<SupplierOrder> {
    const res = await doFetch(`${BASE}/orders/${orderId}/items/${itemId}/accept`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(input),
    });
    return handle<SupplierOrder>(res);
}

/** 发货 for one product — each is accepted and made separately. */
export async function shipSupplierOrderItem(
    orderId: number, itemId: number,
    input: { tracking_number: string; shipping_carrier?: string },
): Promise<SupplierOrder> {
    const res = await doFetch(`${BASE}/orders/${orderId}/items/${itemId}/ship`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(input),
    });
    return handle<SupplierOrder>(res);
}

// ── 我的 (store profile) ──────────────────────────────────────────────────────
export interface SupplierProfile {
    id: number;
    name: string;
    avatar_url: string | null;
    phone: string | null;
    wechat: string | null;
    email: string | null;
    suitable_plants: string[];
    pot_style: string | null;
    online_channel: { type: string | null; url: string | null };
    brand_story: string | null;
    location: { country: string | null; city: string | null };
    payment: {
        /** 银行留存的号码，未必是登录用的手机号。 */
        phone: string | null;
        recipient: string | null;
        account: string | null;
        bank: string | null;
        branch: string | null;
        /** 联行号：同一家银行在不同城市有同名支行，只有它是唯一的。 */
        branch_code: string | null;
    };
    updated_at: string | null;
}

export type SupplierProfileInput = Partial<Pick<SupplierProfile,
    'name' | 'avatar_url' | 'phone' | 'wechat' | 'email' | 'suitable_plants' |
    'pot_style' | 'online_channel' | 'brand_story' | 'location' | 'payment'>>;

export const POT_STYLES = ['自然', '古陶', '雕塑', '其他'] as const;
export const ONLINE_CHANNEL_TYPES = ['官网', '淘宝', '天猫', '闲鱼', '小红书'] as const;

export async function getSupplierProfile(): Promise<SupplierProfile> {
    const res = await doFetch(`${BASE}/profile`, { headers: authHeaders() });
    return handle<SupplierProfile>(res);
}

export async function updateSupplierProfile(data: SupplierProfileInput): Promise<SupplierProfile> {
    const res = await doFetch(`${BASE}/profile`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(data),
    });
    return handle<SupplierProfile>(res);
}

// ── Products ─────────────────────────────────────────────────────────────────
export interface ProductBrand { id: number; name: string }

/** 共享的品牌字典。App 录品和网页货盘写的是同一张表。 */
export async function listProductBrands(): Promise<ProductBrand[]> {
    const res = await doFetch(`${BASE}/brands`, { headers: authHeaders() });
    return (await handle<{ brands: ProductBrand[] }>(res)).brands;
}

export interface SupplierProductPage { products: SupplierProduct[]; page?: number; pages?: number; total?: number }
export async function listSupplierProducts(supplierId?: number, options?: { page?: number; per_page?: number; signal?: AbortSignal }): Promise<SupplierProductPage> {
    const q = new URLSearchParams();
    if (supplierId != null) q.set('supplier_id', String(supplierId));
    if (options?.page) q.set('page', String(options.page));
    if (options?.per_page) q.set('per_page', String(options.per_page));
    const url = `${BASE}/products${q.toString() ? `?${q}` : ''}`;
    return readQuery(`raw|${url}`, async signal => handle<SupplierProductPage>(await doFetch(url, { headers: authHeaders(), signal })), options);
}

export async function getSupplierProduct(id: number, supplierId?: number, signal?: AbortSignal): Promise<SupplierProduct> {
    const query = supplierId == null ? '' : `?supplier_id=${supplierId}`;
    return handle<SupplierProduct>(await doFetch(`${BASE}/products/${id}${query}`, { headers: authHeaders(), signal }));
}

// FormData: photo (required), bottom_photo?, other_photos[]?, and text fields.
export async function createSupplierProduct(form: FormData): Promise<SupplierProduct> {
    const res = await doFetch(`${BASE}/products`, {
        method: 'POST',
        headers: authHeaders(), // no Content-Type → browser sets multipart boundary
        body: form,
    });
    return handle<SupplierProduct>(res);
}

export interface SupplierProductInput {
    name?: string | null;
    supply_price?: number | null;
    retail_price?: number | null;
    quantity?: number;
    supplier_sku?: string | null;
    internal_size_cm?: { length?: number | null; width?: number | null; height?: number | null };
    external_size_cm?: { length?: number | null; width?: number | null; height?: number | null };
    weight?: number | null;
    attributes?: SupplierProduct['attributes'];
    category_id?: number | null;
    product_labels?: string[];
    product_note?: string | null;
    /** 按名字解析：没有这个品牌就新建一条，与 App 共用 product_brands。 */
    brand_name?: string | null;
    client_id?: string;
    // legacy flat aliases still accepted by the backend
    price?: number | null;
    height?: number | null; width?: number | null; depth?: number | null;
}

// Create via JSON (app-style: fields only, photos uploaded later via upload-token).
export async function createSupplierProductJson(data: SupplierProductInput): Promise<SupplierProduct> {
    const res = await doFetch(`${BASE}/products`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(data),
    });
    return handle<SupplierProduct>(res);
}

export async function updateSupplierProduct(
    id: number,
    data: SupplierProductInput,
): Promise<SupplierProduct> {
    const res = await doFetch(`${BASE}/products/${id}`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(data),
    });
    return handle<SupplierProduct>(res);
}

// ── Photos: 七牛 upload-token (presign) direct-upload flow ────────────────────
export async function requestPhotoUploadTicket(itemId: number, slot: PhotoSlot): Promise<PhotoUploadTicket> {
    const res = await doFetch(`${BASE}/products/${itemId}/photos/upload-token`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ slot }),
    });
    return handle<PhotoUploadTicket>(res);
}

export async function completePhotoUpload(
    itemId: number, input: { slot: PhotoSlot; key: string; public_url: string },
): Promise<SupplierProduct> {
    const res = await doFetch(`${BASE}/products/${itemId}/photos/complete`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(input),
    });
    return handle<SupplierProduct>(res);
}

export async function deleteSupplierProduct(id: number): Promise<{ deleted: boolean; id: number }> {
    const res = await doFetch(`${BASE}/products/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    return handle<{ deleted: boolean; id: number }>(res);
}

// ── Team ────────────────────────────────────────────────────────────────────
export async function listSuppliersForTeam(options?: { signal?: AbortSignal }): Promise<{ suppliers: SupplierSummary[] }> {
    const url = `${BASE}/suppliers`;
    return readQuery(`raw|${url}`, async signal => handle<{ suppliers: SupplierSummary[] }>(await doFetch(url, { headers: authHeaders(), signal })), options);
}

// Team-only: create a supplier (name + optional 访问码).
export async function teamCreateSupplier(
    data: { name: string; access_code?: string; phone?: string; password?: string },
): Promise<SupplierSummary> {
    const res = await doFetch(`${BASE}/suppliers`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(data),
    });
    return handle<SupplierSummary>(res);
}

// Team-only: rename a supplier and/or set/reset their 访问码 (empty code clears it).
export async function teamUpdateSupplier(
    id: number,
    data: { name?: string; access_code?: string; phone?: string; password?: string },
): Promise<SupplierSummary> {
    const res = await doFetch(`${BASE}/suppliers/${id}`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(data),
    });
    return handle<SupplierSummary>(res);
}
